import fetchWithAuth from "./secureFetch";

export interface PortalSessionResponse {
    url: string;
    success: boolean;
}

export interface PortalSessionError {
    error: string;
    message?: string;
}

/**
 * Creates a Stripe Customer Portal session and returns the URL.
 * The portal allows users to manage their subscription, update payment methods,
 * view invoices, and cancel their subscription.
 *
 * @param token - The authentication token
 * @param returnUrl - Optional URL to redirect to after leaving the portal
 * @returns Promise with the portal URL or throws an error
 */
export async function createBillingPortalSession(
    token: string,
    returnUrl?: string
): Promise<string> {
    const response = await fetchWithAuth("/api/billing/create-portal-session", token, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            return_url: returnUrl || window.location.origin
        })
    });

    const data = await response.json();

    if (!response.ok) {
        const errorData = data as PortalSessionError;
        throw new Error(errorData.message || errorData.error || "Failed to create portal session");
    }

    const successData = data as PortalSessionResponse;
    return successData.url;
}

/**
 * Opens the Stripe Customer Portal in a new window or redirects to it.
 *
 * @param token - The authentication token
 * @param returnUrl - Optional URL to redirect to after leaving the portal
 * @param openInNewTab - Whether to open in a new tab (default: false, redirects current page)
 */
export async function openBillingPortal(
    token: string,
    returnUrl?: string,
    openInNewTab: boolean = false
): Promise<void> {
    const portalUrl = await createBillingPortalSession(token, returnUrl);

    if (openInNewTab) {
        window.open(portalUrl, "_blank");
    } else {
        window.location.href = portalUrl;
    }
}
